import { useEffect } from 'react'

type ZendeskLoadTrigger = 'idle' | 'interaction'

const ZENDESK_SCRIPT_ID = 'ze-snippet'
const ZENDESK_ACCOUNT_KEY = process.env.NEXT_PUBLIC_ZENDESK_ACCOUNT_KEY

let zendeskLoadPromise: Promise<void> | null = null
let zendeskLoadScheduled = false
let chatOpenedByUser = false

function getSrc(key?: string): string | undefined {
  return key ? `https://static.zdassets.com/ekr/snippet.js?key=${key}` : undefined
}

function markOnce(markName: string): void {
  if (typeof performance === 'undefined') return
  if (!performance.getEntriesByName(markName).length) {
    performance.mark(markName)
  }
}

function measureLoad(trigger: ZendeskLoadTrigger): void {
  if (typeof performance === 'undefined') return

  const startMark = `zendesk-load-requested:${trigger}`
  const endMark = 'zendesk-ready'

  if (
    performance.getEntriesByName(startMark).length &&
    performance.getEntriesByName(endMark).length
  ) {
    const measureName = `zendesk-load:${trigger}`
    if (!performance.getEntriesByName(measureName).length) {
      performance.measure(measureName, startMark, endMark)
    }
  }
}

function waitForZE(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window.zE !== 'undefined') {
      resolve()
      return
    }

    const t = window.setInterval(() => {
      if (typeof window.zE !== 'undefined') {
        window.clearInterval(t)
        resolve()
      }
    }, 50)
  })
}

function suppressMobileLauncher(): void {
  if (!isMobile()) return

  const win = window as Window & typeof globalThis & { zESettings?: unknown }
  win.zESettings = {
    webWidget: {
      launcher: { mobile: { labelVisible: false } },
      chat: { notifications: { mobile: { disable: true } } },
    },
  }
}

function injectZendeskScript(key?: string): void {
  const src = getSrc(key)
  if (!src || document.getElementById(ZENDESK_SCRIPT_ID)) return

  suppressMobileLauncher()

  const script = document.createElement('script')
  script.id = ZENDESK_SCRIPT_ID
  script.async = true
  script.defer = true
  script.src = src
  document.body.appendChild(script)
}

function isMobile(): boolean {
  return typeof window !== 'undefined' && window.innerWidth <= 768
}

function safeZE(...args: Parameters<typeof window.zE>): void {
  try {
    if (typeof window.zE === 'function') {
      window.zE(...args)
    }
  } catch {
    /* widget type not available */
  }
}

function neutralizeZendeskContainers(): void {
  const selectors = [
    '#Embed',
    'div[data-product="web_widget"]',
    '.zEWidget-launcher',
    '[data-testid="launcher"]',
  ]
  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.pointerEvents = 'none'
      }
    })
  })
}

function hideOnMobile(): void {
  if (!isMobile() || typeof window.zE !== 'function') return

  safeZE('messenger', 'hide')
  safeZE('webWidget', 'hide')
  neutralizeZendeskContainers()

  // Re-hide when proactive messages arrive (they can re-show the widget)
  // but not when the user explicitly opened the chat
  try {
    window.zE('messenger:on', 'unreadMessages', () => {
      if (!chatOpenedByUser) {
        safeZE('messenger', 'hide')
        neutralizeZendeskContainers()
      }
    })
  } catch {
    /* messenger events not supported */
  }
  try {
    window.zE('webWidget:on', 'chat:unreadMessages', () => {
      if (!chatOpenedByUser) {
        safeZE('webWidget', 'hide')
        neutralizeZendeskContainers()
      }
    })
  } catch {
    /* webWidget events not supported */
  }
}

function showAndOpen(): void {
  chatOpenedByUser = true

  if (isMobile()) {
    safeZE('messenger', 'show')
    safeZE('webWidget', 'show')
  }

  safeZE('messenger', 'open')
  safeZE('webWidget', 'open')
}

function setOpeners(): void {
  window.openSupportChat = showAndOpen
}

function loadZendesk(
  key?: string,
  trigger: ZendeskLoadTrigger = 'idle',
): Promise<void> | undefined {
  if (!key || typeof window === 'undefined') return undefined

  if (zendeskLoadPromise) return zendeskLoadPromise

  zendeskLoadPromise = new Promise((resolve) => {
    markOnce('zendesk-load-requested')
    markOnce(`zendesk-load-requested:${trigger}`)

    injectZendeskScript(key)

    waitForZE().then(() => {
      markOnce('zendesk-ready')
      measureLoad(trigger)
      setOpeners()
      hideOnMobile()
      resolve()
    })
  })

  return zendeskLoadPromise
}

export const scheduleZendeskLoad = (key: string | undefined = ZENDESK_ACCOUNT_KEY): void => {
  if (!key || typeof window === 'undefined' || zendeskLoadScheduled) return

  zendeskLoadScheduled = true

  const requestIdleCallbackPolyfill = (callback: () => void): number =>
    window.setTimeout(callback, 1500)

  const idleLoader = (): void => {
    loadZendesk(key, 'idle')
  }

  const ric = (
    window as Window &
      typeof globalThis & {
        requestIdleCallback?: (cb: () => void, options?: IdleRequestOptions) => number
      }
  ).requestIdleCallback

  if (ric) {
    ric(idleLoader, { timeout: 5000 })
  } else {
    requestIdleCallbackPolyfill(idleLoader)
  }
}

export const useZendesk = (options: { enabled?: boolean } = {}): void => {
  const { enabled = true } = options

  useEffect(() => {
    if (!enabled) return
    scheduleZendeskLoad()
  }, [enabled])
}

export const openChat = (): void => {
  if (typeof window === 'undefined') return

  if (typeof window.zE !== 'undefined') {
    showAndOpen()
    return
  }

  const loadPromise = loadZendesk(ZENDESK_ACCOUNT_KEY, 'interaction')
  loadPromise?.then(showAndOpen).catch(() => {})
}
